import { type Country } from '@capsicum/common';
import { useAtom } from 'jotai';

import { placeOrderPayloadAtom } from '../../data-access';
import { type OrderFieldKey } from '../../models/orders';

export const useProductOrderDetails = () => {
	const [placeOrderDetails] = useAtom(placeOrderPayloadAtom);

	const merchandiseDetails = () => {
		if (Object.keys(placeOrderDetails?.deliveryDetails || [])[0] === 'merchandise_1') {
			const deliveryDetails: Array<string> = [];
			const detailToRowMapping: Record<string, number> = {
				// Country
				'04cf6d8f-8f30-4de0-98ef-4817dcef4fde': 5,
				// City
				'0c230143-09ae-4d3a-8139-7a4077fb9da9': 3,
				// Street Name and Number
				'73606805-e4f8-4305-803a-eb0e86661430': 2,
				// Area Code
				'9001c1dd-9907-4ecd-b839-04104ac58263': 4,
				// Region
				'd4e85cbc-4a9c-495d-858f-11cfca9b517d': 3,
				// Last Name
				'ddbd6a39-33d0-4592-95e9-a8f24775437f': 1,
				// Apartment, Suite, Building Number
				'f4c63ff7-f2e6-407a-adad-db668bc8bb88': 2,
				// First Name
				'faaf8c66-87eb-469b-9d34-db0cad2a5b50': 1
			};

			Object.values(placeOrderDetails?.deliveryDetails || [])[0]?.forEach(detail => {
				// Other custom fields will be added as row 6, wrapped
				const renderingRow = detailToRowMapping[detail.id!] || 6;

				if (deliveryDetails[renderingRow]) {
					deliveryDetails[renderingRow] += detail.value ? ` ${detail.value}` : '';
				} else {
					deliveryDetails[renderingRow] = detail.value || '';
				}
			});

			// Change countryObject to fully translated version
			const countryObject = deliveryDetails[5] as unknown as Country;

			deliveryDetails[5] = countryObject?.name || '';

			// Add commas
			const detailsWithSpaces = deliveryDetails.map((detail, idx) => {
				if (idx > 1 && idx < deliveryDetails.length - 1) {
					return detail + ', ';
				}

				return detail;
			});

			return detailsWithSpaces.slice(2);
		}

		return;
	};

	const getFieldByCategory = (fieldCategory: OrderFieldKey, fallbackValue: string) => {
		if (!placeOrderDetails?.deliveryDetails) {
			return fallbackValue;
		}

		const fieldByCategory = Object.values(placeOrderDetails?.deliveryDetails)[0]?.find(
			field => field.fieldCategory === fieldCategory
		)?.value;

		return fieldByCategory || fallbackValue;
	};

	const recipientName =
		Object.values(placeOrderDetails?.deliveryDetails || [])[0]?.find(item => item.displayOrder === 3)?.value || '';

	const ticketHolders = () => {
		const ticketDetails: Array<Record<string, string>> = [];

		Object.values(placeOrderDetails?.deliveryDetails || []).forEach(ticketDetail => {
			ticketDetails.push({
				firstname: ticketDetail.find(item => item.displayOrder === 1)?.value || '',
				lastname: ticketDetail.find(item => item.displayOrder === 2)?.value || ''
			});
		});

		return ticketDetails;
	};

	return {
		getFieldByCategory,
		merchandiseDetails,
		recipientName,
		ticketHolders
	};
};
